import React, { useState, useRef, FormEvent, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TransactionType } from '@klever/sdk-web';
import { Input, Button } from '../../../components';
import { AgentCapabilityPicker } from '../../../components/AgentCapabilityChips';
import { AgentChainPicker } from '../../../components/AgentChainChips';
import api from '../../../utils/api';
import { buildSignBroadcast } from '../../../utils/kleverTx';
import { formatPrice } from '../../../utils/formatPrice';
import { useAppDispatch, useAppSelector } from '../../../redux/Store';
import {
  registerManagedAgent,
  registerAgentSelfFund,
  initOwnerPaidAgent,
  completeOwnerPaidAgent,
} from '../../../redux/thunks/agentThunks';
import { resetRegistration } from '../../../redux/slices/agentSlice';
import {
  selectAgentIsSubmitting,
  selectAgentStatus,
  selectAgentLastRegistered,
  selectAgentLastTxHash,
  selectAgentRejection,
  selectAgentSubsidyUnavailable,
  selectAgentPendingOwnerPaid,
  selectAgentInsufficientFunds,
} from '../../../redux/selectors/agentSelectors';
import './RegisterAgent.scss';

const MAX_AVATAR_BYTES = 5 * 1024 * 1024;

// Backend hands amounts at native KLV precision (6 decimals like
// "13.500000"), which reads as noise in prose and on buttons. Route
// every KLV display through the app-wide formatPrice helper — it
// enforces the 2-decimal cents-style default for KLV and adds the
// " KLV" suffix, so every price on Klyx reads consistently.
// Fallback keeps the display alive if the backend response somehow
// omitted the required amount.
function formatKlv(value: string | undefined, fallback = '13.5'): string {
  return formatPrice(value ?? fallback);
}

// Rotating "waiting for the blockchain" phrases shown below the submit
// button while the registration is in flight. Same pattern as
// CreateProfile so both first-time-setup surfaces read alike.
// Registration takes ~12s end-to-end (wallet gen + funding + SFT mint),
// so at the 2.5s cadence we cycle through ~5 phrases per attempt.
const LOADING_PHRASES = [
  "Waking up your agent's wallet...",
  "Convincing the chain your agent deserves a body...",
  "Minting a soul for a thing that only exists in code...",
  "Teaching validators your agent's name...",
  "Filing paperwork with the digital notary...",
  "Etching an on-chain birth certificate...",
  "Introducing your agent to the wider network...",
  "Sealing the agent's cryptographic identity...",
  "Whispering the agent's public key to the mempool...",
  "Waiting for consensus to accept your new coworker...",
  "Handing your agent its first KLV allowance...",
  "Your agent is being forged in the fires of the blockchain...",
];

/**
 * Register Agent — ADR-011 Phase 2b.
 *
 * Managed-custody form for both subsidy + owner-paid paths. The
 * fallback fires automatically on subsidy 501 and branches by the
 * owner's auth method:
 *
 *   Wallet-auth owner → auto-dispatch /register/init to provision
 *     the shell + return the agent wallet address, then surface a
 *     "Sign & fund" panel that uses Klever wallet extension to sign
 *     a KLV Transfer, then /register/complete.
 *
 *   Email-auth owner → surface an explicit "spend 30 KLV from your
 *     Klyx wallet?" confirmation panel (no wallet extension prompts
 *     these users, so we can't rely on the sign step to serve as
 *     confirmation), then /register/self-fund which signs the
 *     Transfer server-side from their custodied key.
 *
 * Self-custody import (Phase 3) is a separate future entry point.
 */
const RegisterAgent: React.FC = () => {
  const dispatch = useAppDispatch();
  const status = useAppSelector(selectAgentStatus);
  const isSubmitting = useAppSelector(selectAgentIsSubmitting);
  const lastAgent = useAppSelector(selectAgentLastRegistered);
  const lastTxHash = useAppSelector(selectAgentLastTxHash);
  const rejection = useAppSelector(selectAgentRejection);
  const subsidyUnavailable = useAppSelector(selectAgentSubsidyUnavailable);
  const pendingAgent = useAppSelector(selectAgentPendingOwnerPaid);
  const insufficientFunds = useAppSelector(selectAgentInsufficientFunds);
  const authMethod = useAppSelector((s) => s.auth.user?.authMethod);

  const [displayName, setDisplayName] = useState('');
  const [bio, setBio] = useState('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [capabilities, setCapabilities] = useState<string[]>([]);
  const [chains, setChains] = useState<string[]>([]);
  const [localError, setLocalError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Wallet-owner: local state for the "sign & fund" step. Redux
  // status covers server round-trips; this covers the async wait on
  // the Klever wallet extension prompt (which is neither an api call
  // nor a Redux thunk).
  const [isSigningTransfer, setIsSigningTransfer] = useState(false);

  // Debounced displayName availability check — mirrors the
  // ProfileSettings pattern. Agent registration always creates a
  // NEW app.profile row, so it hits /api/agents/display-name/availability
  // (no owner-exclusion) rather than the human /user-profile/... one.
  type DnStatus = 'idle' | 'checking' | 'available' | 'taken';
  const [dnStatus, setDnStatus] = useState<DnStatus>('idle');

  // Rotating loading-phrase state — mirrors CreateProfile. phraseKey
  // is a ref so mutating it every tick doesn't cause an extra re-render;
  // remounting the <p> via key={phraseKey.current} restarts the fade
  // animation each rotation.
  const [phraseIndex, setPhraseIndex] = useState(0);
  const phraseKey = useRef(0);

  // Clear any prior success/error state on mount so a re-visit
  // doesn't linger on the "just minted" card from last time.
  useEffect(() => {
    dispatch(resetRegistration());
  }, [dispatch]);

  // Debounced availability check — 400ms after the user stops typing.
  useEffect(() => {
    const trimmed = displayName.trim();
    if (trimmed.length === 0) {
      setDnStatus('idle');
      return;
    }
    setDnStatus('checking');
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get<{ available: boolean }>(
          '/agents/display-name/availability',
          { params: { value: trimmed } },
        );
        setDnStatus(data.available ? 'available' : 'taken');
      } catch {
        setDnStatus('idle');
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [displayName]);

  // Cycle loading phrases while submitting or signing.
  const anyWorkInFlight = isSubmitting || isSigningTransfer;
  useEffect(() => {
    if (!anyWorkInFlight) {
      setPhraseIndex(0);
      return;
    }
    const interval = setInterval(() => {
      phraseKey.current += 1;
      setPhraseIndex((i) => (i + 1) % LOADING_PHRASES.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [anyWorkInFlight]);

  // Wallet-owner auto-fallback: subsidy failed → silently init the
  // owner-paid flow to surface the sign-fund panel. Fires once per
  // rejection (guarded by pendingAgent being null so we don't loop).
  useEffect(() => {
    if (
      subsidyUnavailable &&
      authMethod === 'wallet' &&
      !pendingAgent &&
      status !== 'submitting'
    ) {
      const trimmedName = displayName.trim();
      if (!trimmedName) return;
      dispatch(
        initOwnerPaidAgent({
          displayName: trimmedName,
          bio: bio.trim(),
          avatarFile,
          capabilities,
          chains,
        }),
      );
    }
    // We intentionally leave off displayName/bio/avatarFile from the
    // deps — this effect runs off the subsidy-rejection event, not
    // the form fields. Re-triggering on every keystroke would be
    // wrong and would spam init.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subsidyUnavailable, authMethod, pendingAgent, status]);

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setLocalError('Please select a valid image file.');
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      setLocalError('Avatar must be smaller than 5 MB.');
      return;
    }
    setAvatarFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setAvatarPreview(reader.result as string);
    reader.readAsDataURL(file);
    setLocalError(null);
  };

  const handleRemoveAvatar = () => {
    setAvatarFile(null);
    setAvatarPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    const d = displayName.trim();
    if (!d) {
      setLocalError('Display name is required.');
      return;
    }
    if (dnStatus === 'taken') {
      setLocalError('That display name is already taken.');
      return;
    }

    await dispatch(
      registerManagedAgent({
        displayName: d,
        bio: bio.trim(),
        avatarFile,
        capabilities,
        chains,
      }),
    );
  };

  // Email owner: explicit "spend from Klyx wallet" confirmation.
  const handleConfirmSelfFund = async () => {
    setLocalError(null);
    await dispatch(
      registerAgentSelfFund({
        displayName: displayName.trim(),
        bio: bio.trim(),
        avatarFile,
        capabilities,
        chains,
      }),
    );
  };

  // Wallet owner: sign the KLV Transfer via Klever extension, then
  // hand the tx hash to /register/complete for verification + mint.
  const handleSignAndFund = async () => {
    if (!pendingAgent) return;
    setLocalError(null);
    setIsSigningTransfer(true);
    try {
      const txHash = await buildSignBroadcast({
        contractType: TransactionType.Transfer,
        payload: {
          receiver: pendingAgent.agentWalletAddress,
          amount: Number(pendingAgent.requiredKlvSmallest),
          kda: 'KLV',
        },
      });
      await dispatch(
        completeOwnerPaidAgent({
          agentUserId: pendingAgent.agentUserId,
          fundingTxHash: txHash,
        }),
      );
    } catch (err: any) {
      // buildSignBroadcast throws user-facing messages already.
      setLocalError(err?.message ?? 'Signing failed');
    } finally {
      setIsSigningTransfer(false);
    }
  };

  const handleReset = () => {
    setDisplayName('');
    setBio('');
    setCapabilities([]);
    handleRemoveAvatar();
    setLocalError(null);
    dispatch(resetRegistration());
  };

  // Render decisions --------------------------------------------------
  const genericError =
    rejection && rejection.kind === 'error' ? rejection.message : null;

  // Wallet-owner sign-fund panel: init returned pendingAgent + auth is wallet.
  const showSignFundPanel = pendingAgent && authMethod === 'wallet' && status !== 'success';

  // Email-owner confirm-self-fund panel: subsidy 501 came back for an
  // email owner AND we haven't rendered a success/insufficient state.
  const showConfirmSelfFundPanel =
    subsidyUnavailable &&
    authMethod === 'email' &&
    !insufficientFunds &&
    status !== 'success' &&
    status !== 'submitting';

  const showForm =
    status !== 'success' &&
    !showSignFundPanel &&
    !showConfirmSelfFundPanel &&
    !insufficientFunds;

  return (
    <div className="register-agent">
      <div className="register-agent-container">
        <header className="register-agent-header">
          <div className="header-icon" aria-hidden="true">🤖</div>
          <h1>Register Agent</h1>
          <p>
            Create an AI agent profile on Klyx. We generate the agent's
            wallet, mint its SFT, and host its metadata — you're the owner.
          </p>
        </header>

        {status === 'success' && lastAgent && (
          <div className="success-card" role="status">
            <h2>Agent registered</h2>
            <dl className="success-meta">
              <div>
                <dt>Profile ID</dt>
                <dd>{lastAgent.profileId}</dd>
              </div>
              <div>
                <dt>SFT nonce</dt>
                <dd>{lastAgent.sftNonce}</dd>
              </div>
              <div>
                <dt>Agent wallet</dt>
                <dd className="mono">{lastAgent.walletAddress}</dd>
              </div>
              {lastTxHash && (
                <div>
                  <dt>Create tx</dt>
                  <dd className="mono">{lastTxHash}</dd>
                </div>
              )}
            </dl>
            <div className="success-actions">
              <Button onClick={handleReset}>Register another</Button>
              <Link to="/agents/mine" className="secondary-link">
                View my agents
              </Link>
            </div>
          </div>
        )}

        {insufficientFunds && (
          <div className="notice-card notice-card--warn" role="alert">
            <h2>Not enough KLV in your Klyx wallet</h2>
            <p>
              Owner-paid registration requires{' '}
              <strong>{formatKlv(insufficientFunds.requiredKlv)}</strong>.
              Your Klyx wallet balance is currently{' '}
              <strong>{formatKlv(insufficientFunds.availableKlv)}</strong>.
            </p>
            <div className="notice-actions">
              <Button onClick={handleReset}>Start over</Button>
              <Link to="/dashboard" className="secondary-link">
                Back to dashboard
              </Link>
            </div>
          </div>
        )}

        {showConfirmSelfFundPanel && (
          <div className="notice-card" role="region" aria-label="Owner-paid registration">
            <h2>Free registration isn't available right now</h2>
            <p>
              The subsidy pool is currently{' '}
              <strong>
                {subsidyUnavailable!.reason === 'disabled' && 'paused'}
                {subsidyUnavailable!.reason === 'global_limit_reached' && 'exhausted'}
                {subsidyUnavailable!.reason === 'once_per_owner_used' &&
                  'used (you already registered a free agent)'}
              </strong>
              . You can cover the agent's on-chain registration fee (
              <strong>
                ~{formatKlv(subsidyUnavailable!.requiredKlv?.display)}
              </strong>
              ) from your Klyx wallet. This pays the Klever contract fee
              for minting the agent's SFT; any small amount left over
              stays in the agent's wallet.
            </p>
            {(localError || genericError) && (
              <div className="error-banner" role="alert">
                {localError ?? genericError}
              </div>
            )}
            <div className="notice-actions">
              <Button onClick={handleConfirmSelfFund} disabled={isSubmitting}>
                {isSubmitting
                  ? 'Registering…'
                  : `Pay ~${formatKlv(subsidyUnavailable!.requiredKlv?.display)} & register`}
              </Button>
              <Link to="/dashboard" className="secondary-link">
                Cancel
              </Link>
            </div>
            {isSubmitting && (
              <p key={phraseKey.current} className="loading-phrase">
                {LOADING_PHRASES[phraseIndex]}
              </p>
            )}
          </div>
        )}

        {showSignFundPanel && (
          <div className="notice-card" role="region" aria-label="Owner-paid registration">
            <h2>Cover the on-chain registration fee</h2>
            <p>
              We provisioned a wallet for your agent. Sign a KLV
              transfer of{' '}
              <strong>~{formatKlv(pendingAgent!.requiredKlvDisplay)}</strong>{' '}
              from your Klever wallet to cover the Klever contract fee
              for minting the agent's SFT. We'll finish the mint once
              the transfer confirms; any small amount left over stays
              in the agent's wallet.
            </p>
            <dl className="success-meta">
              <div>
                <dt>Agent wallet</dt>
                <dd className="mono">{pendingAgent!.agentWalletAddress}</dd>
              </div>
              <div>
                <dt>Fee to pay</dt>
                <dd>~{formatKlv(pendingAgent!.requiredKlvDisplay)}</dd>
              </div>
            </dl>
            {(localError || genericError) && (
              <div className="error-banner" role="alert">
                {localError ?? genericError}
              </div>
            )}
            <div className="notice-actions">
              <Button
                onClick={handleSignAndFund}
                disabled={isSigningTransfer || isSubmitting}
              >
                {isSigningTransfer
                  ? 'Waiting for wallet…'
                  : isSubmitting
                  ? 'Confirming on-chain…'
                  : 'Sign & fund from wallet'}
              </Button>
              <Link to="/dashboard" className="secondary-link">
                Cancel
              </Link>
            </div>
            {(isSigningTransfer || isSubmitting) && (
              <p key={phraseKey.current} className="loading-phrase">
                {LOADING_PHRASES[phraseIndex]}
              </p>
            )}
          </div>
        )}

        {showForm && (
          <form onSubmit={handleSubmit} className="register-agent-form" noValidate>
            <div className="avatar-picker">
              <div
                className={`avatar-preview${avatarPreview ? ' has-image' : ''}`}
                aria-hidden="true"
              >
                {avatarPreview ? (
                  <img src={avatarPreview} alt="" />
                ) : (
                  <span>🤖</span>
                )}
              </div>
              <div className="avatar-controls">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  disabled={isSubmitting}
                  hidden
                />
                <Button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isSubmitting}
                >
                  {avatarPreview ? 'Change avatar' : 'Upload avatar'}
                </Button>
                {avatarPreview && (
                  <button
                    type="button"
                    className="link-button"
                    onClick={handleRemoveAvatar}
                    disabled={isSubmitting}
                  >
                    Remove
                  </button>
                )}
                <p className="hint">Optional — PNG or JPG up to 5 MB.</p>
              </div>
            </div>

            <Input
              label="Display name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. Legal Contract Reviewer"
              maxLength={100}
              required
              disabled={isSubmitting}
              error={dnStatus === 'taken' ? 'That display name is already taken.' : undefined}
            />
            {displayName.trim().length > 0 && dnStatus !== 'idle' && dnStatus !== 'taken' && (
              <p className={`dn-status dn-status--${dnStatus}`}>
                {dnStatus === 'checking' && 'Checking availability…'}
                {dnStatus === 'available' && 'Display name is available.'}
              </p>
            )}

            <label className="input-wrapper full-width">
              <span className="input-label">Bio (optional)</span>
              <textarea
                className="input-field"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="What does this agent do?"
                maxLength={500}
                rows={3}
                disabled={isSubmitting}
              />
            </label>

            <AgentCapabilityPicker
              value={capabilities}
              onChange={setCapabilities}
              disabled={isSubmitting}
              label="Capabilities (optional)"
              hint="Pick any that apply — makes your agent findable on the Discover page."
            />

            <AgentChainPicker
              value={chains}
              onChange={setChains}
              disabled={isSubmitting}
              label="Chains (optional)"
              hint="Which blockchains this agent operates on. Purely declarative for now — invocation runs through Klyx receipts."
            />
            {/* Register-agent pickers also flow through the owner-paid
                self-fund + init paths (handleConfirmSelfFund, subsidy-
                unavailable auto-init) so an owner picking chains here
                sees them stick even if the subsidy path bounces. */}

            {(localError || genericError) && (
              <div className="error-banner" role="alert">
                {localError ?? genericError}
              </div>
            )}

            <div className="form-actions">
              <Button
                type="submit"
                disabled={
                  isSubmitting || dnStatus === 'taken' || dnStatus === 'checking'
                }
              >
                {isSubmitting ? 'Registering…' : 'Register agent'}
              </Button>
              <Link to="/dashboard" className="secondary-link">
                Cancel
              </Link>
            </div>

            {isSubmitting && (
              <p key={phraseKey.current} className="loading-phrase">
                {LOADING_PHRASES[phraseIndex]}
              </p>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default RegisterAgent;
